import { User } from '../../../types';

/**
 * Function to get a zero-indexed cell number from a pair of zero-indexed coordinates
 * for a game board of a specified width.
 *
 * @param coords - The zero-indexed coordinateds.
 * @param boardWidth - The width of the game board.
 * @returns the zero-indexed cell number.
 */
export const coordsToNum = (coords: number[], boardWidth: number): number =>
  coords[0] * boardWidth + coords[1];

/**
 * Function to get a pair of zero-indexed from a zero-indexed cell number
 * for a game board of a specified width.
 *
 * @param num - The zero-indexed cell number.
 * @param boardWidth - The width of the game board.
 * @returns the pair of zero-indexed coordinates.
 */
export const numToCoords = (num: number, boardWidth: number) => [
  Math.floor(num / boardWidth),
  num % boardWidth,
];

/**
 * Function to get the status text to display in the modal indicating whose turn
 * it is, or whether the user has won, lost, or tied.
 * for a game board of a specified width.
 *
 * @param gameOver - Boolean representing whether or not the game is over.
 * @param gameResult - Array containing the information for the previous turn.
 * @param opponent - The opponent user object.
 * @param canPlay - Boolean representing whether or not the user can take their turn.
 * @returns the status text to display.
 */
export const getStatusText = (
  gameOver: boolean,
  gameResult: (string | number[])[],
  opponent: User | undefined,
  canPlay: boolean,
) => {
  if (gameOver) {
    if (gameResult[0] === 'quit') return `${opponent?.username || ''} has quit.`;
    if (gameResult[0] === 'unable' || gameResult[0] === 'exit')
      return `Unable to play ${opponent?.username || ''}`;
    return `You ${gameResult[0]}${gameResult[0] === 'won' ? '!' : '.'}`;
  }
  if (canPlay) return 'Your turn!';
  return 'Waiting for opponent.';
};

/**
 * Function to get the buttons occupied by the specified user based on the current
 * status of the game.
 *
 * @param occId - The ID of the user for whom the buttons are being checked for occupancy.
 * @param gameStatus - Array containing information for every turn taken in the game.
 * @param boardWidth - The width of the game board.
 * @returns the buttons currently occupied by the specified user.
 */
export const getOccButtons = (
  occId: string,
  gameStatus: (string | number | number[][])[][],
  boardWidth: number,
) =>
  gameStatus
    .filter(data => data.length === 3 && data[0] === occId)
    .map(data => coordsToNum([data[1], data[2]] as number[], boardWidth));
